import { getApiUrl } from '../config/api-config';

/**
 * Server-rendered page images of the flattened form.
 *
 * Design section 3.3.1: an anonymous visitor **may see** the flattened result
 * as proof the product works, but the download must be reasonably prevented.
 * The server flattens as normal, rasterises the result and returns the pixels -
 * so what arrives here has no text layer, no form fields and no vectors.
 * Nothing that functions as a document ever reaches the browser.
 *
 * This is why the preview must NOT be built by rendering a returned PDF: the
 * moment the file is on the client, the browser's own viewer offers a save
 * button and the network tab offers the bytes. Client-side download
 * suppression is theatre.
 */
export interface FlattenedPreview {
  /** One base64-encoded PNG per page, in page order. */
  pages: string[];
  /**
   * Pages in the whole document, which may exceed those rendered.
   *
   * The server caps a preview at five pages - a guard against an unauthenticated
   * visitor turning a 400-page upload into server time. Reported so the UI can
   * say "showing the first 5 of 12" rather than silently dropping the rest.
   */
  totalPages: number;
  /** The DPI the server rendered at, for the caption. */
  dpi: number;
}

/** A refusal that carries the server's own wording, so the UI never invents one. */
export class FlattenedPreviewError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'FlattenedPreviewError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Render the current form and return its pages as images.
 *
 * Consumes no flattened-PDF allowance - nothing is delivered, so nothing is
 * spent. Gated on `canPreviewFlattened`, which is true for guests, rather than
 * on `canDownloadFlattenedPdf`, which is not. Looking and taking are different
 * permissions, and this is the one that makes "play first" possible.
 */
export async function fetchFlattenedPreview(
  documentId: string,
  formConfiguration: unknown,
  licenseKeyHash?: string | null
): Promise<FlattenedPreview> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (licenseKeyHash) {
    headers['X-License-Key-Hash'] = licenseKeyHash;
  }

  const response = await fetch(
    getApiUrl(`/api/pdf/forms/${documentId}/preview-flattened`),
    {
      method: 'POST',
      // A guest's tenancy is the wsid cookie and nothing else. See getApiUrl.
      credentials: 'include',
      headers,
      body: JSON.stringify(formConfiguration),
    }
  );

  if (!response.ok) {
    // A refusal states its reason in headers that CORS exposes; fall back to
    // the body, then to a generic line. Never guess at the cause.
    const headerMessage = response.headers.get('X-Error-Message');
    const code = response.headers.get('X-Error-Code');
    let message = headerMessage || '';
    if (!message) {
      try {
        const body = await response.json();
        message = body?.error || '';
      } catch {
        /* a non-JSON body is not worth failing differently over */
      }
    }
    throw new FlattenedPreviewError(
      message || 'Could not render the preview. Please try again.',
      response.status,
      code
    );
  }

  const data = await response.json();
  return {
    pages: data.pages ?? [],
    totalPages: data.totalPages ?? (data.pages?.length ?? 0),
    dpi: data.dpi ?? 150,
  };
}
